import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { UseFormReturn } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { createOrder } from '@/lib/api/marketplace'
import { trackEvent } from '@/lib/api/metrics'
import {
  DEFAULT_HASHRATE,
  DEFAULT_RUB_ADVANCE,
  DEFAULT_USD_ADVANCE
} from '@/lib/constants'
import { DeviceOrderData, createDeviceOrderSchema } from '@/lib/schemes'
import { Device, PaymentMethod } from '@/lib/types'

import useOrderEstimate from '@/hooks/marketplace/useOrderEstimate'
import useAuthStore from '@/hooks/stores/useAuthStore'
import useCookies from '@/hooks/stores/useCookies'
import { useFlashStore } from '@/hooks/stores/useFlashStore'
import { useSignInStore } from '@/hooks/stores/useSignInStore'
import useLoading from '@/hooks/useLoading'

export const useDeviceOrderDialog = ({
  isOpen,
  device,
  onClose,
  isInstallment,
  form,
  setDeviceOrderSchema
}: {
  isOpen: boolean
  device: Device
  onClose: () => void
  isInstallment?: boolean
  form: UseFormReturn<DeviceOrderData>
  setDeviceOrderSchema: (value: any) => void
}) => {
  const router = useRouter()
  const { isDemo } = useAuthStore()
  const { sessionId } = useCookies()
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { openDemoModal } = useSignInStore()
  const { isLoading, tryLoad } = useLoading()
  const { setSuccessMessage } = useFlashStore()

  const [prevMaxCount, setPrevMaxCount] = useState<number>(0)

  const { reset, setValue, watch, trigger, getValues, clearErrors } = form

  const isFullMiner = device.type === 'full'
  const isBalancePayment = watch('paymentMethod') === 'balance'
  const orderAmount = watch('orderAmount')
  const isOrderQuantityValid = prevMaxCount
    ? orderAmount <= prevMaxCount && !!orderAmount
    : !!orderAmount
  const useBonusBalance = watch('useBonusBalance')

  const { estimate, isLoading: isEstimateLoading } = useOrderEstimate({
    id: device.id,
    orderAmount,
    advance: watch('advance') || 0,
    currency: device.currency,
    installment: isInstallment,
    isFullMiner,
    useBonusBalance,
    enabled: isOrderQuantityValid && isOpen,
    paymentMethod: watch('paymentMethod')
  })

  const {
    maxHashrate,
    maxCount,
    canPayWithBalance,
    minAdvance: minAdvanceEstimate
  } = estimate || {}

  const currentMinAdvance = useMemo(
    () =>
      minAdvanceEstimate
        ? minAdvanceEstimate / 100
        : device.currency === 'USD'
        ? DEFAULT_USD_ADVANCE
        : DEFAULT_RUB_ADVANCE,
    [minAdvanceEstimate, device.currency]
  )

  useEffect(() => {
    setValue(
      'advance',
      isBalancePayment ? null : getValues('advance') || currentMinAdvance
    )
    if (isBalancePayment) {
      trigger('advance')
    }
  }, [
    isBalancePayment,
    currentMinAdvance,
    device.currency,
    trigger,
    setValue,
    getValues
  ])

  useEffect(() => {
    if (!orderAmount) {
      setValue(
        'orderAmount',
        device.count || device.hashrate || DEFAULT_HASHRATE
      )
    }
  }, [device.hashrate, device.count, orderAmount, setValue])

  useEffect(() => {
    if (!!maxHashrate) {
      setPrevMaxCount(maxHashrate)
    }

    if (!!maxCount) {
      setPrevMaxCount(maxCount)
    }

    if (canPayWithBalance && !getValues('paymentMethod')) {
      setValue('paymentMethod', 'balance')
    }
  }, [
    maxCount,
    maxHashrate,
    canPayWithBalance,
    minAdvanceEstimate,
    setValue,
    getValues
  ])

  useEffect(() => {
    if (prevMaxCount) {
      const deviceOrderSchema = createDeviceOrderSchema(
        prevMaxCount,
        currentMinAdvance
      )

      setDeviceOrderSchema(deviceOrderSchema)
    }
  }, [
    prevMaxCount,
    currentMinAdvance,
    minAdvanceEstimate,
    setDeviceOrderSchema
  ])

  const onSubmit = async ({
    orderAmount,
    advance,
    paymentMethod,
    useBonusBalance
  }: DeviceOrderData) => {
    if (isDemo) {
      openDemoModal()
      setTimeout(onClose, 0)
    } else {
      await tryLoad(async () => {
        const { paymentURL } = await createOrder({
          id: device.id,
          useBonusBalance,
          ...(isFullMiner
            ? { deviceCount: orderAmount }
            : { hashrate: orderAmount }),
          paymentMethod: paymentMethod as PaymentMethod,
          advance: advance as number,
          currency: device.currency,
          installment: isInstallment,
          metricsId: sessionId
        })

        onClose()
        reset()

        if (paymentURL) {
          await trackEvent('payment_redirect')
          await router.push(paymentURL)
        }

        if (isBalancePayment) {
          await router.push('/farm')
          await queryClient.invalidateQueries({
            queryKey: ['user-info']
          })

          setSuccessMessage(
            formatMessage({
              id: 'payment.success',
              defaultMessage: 'Оплата прошла успешно'
            })
          )
        }
      })
    }
  }

  const handleCloseModal = () => {
    reset()
    onClose()
    clearErrors()
  }

  return {
    form,
    estimate,
    isLoading,
    onSubmit,
    handleCloseModal,
    isEstimateLoading: isOrderQuantityValid && isOpen && isEstimateLoading
  }
}
